import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CHANGE_LOCATION_QUICK_REPLIES,
  LOCATION_QUICK_REPLIES,
  TOPIC_QUICK_REPLIES,
  type ChatSearchResponse,
  type ClientSessionResponse,
  type ClientLocation,
  type HomeLayout,
  detectIntents,
  detectLocation,
  displayCity,
  locationLabel,
  parseLocationChoice,
} from '@astrologermatch/shared';
import { Diya, Kalash, Lotus, Moon, RishiAvatar, Sparkle } from '../components/Rishi.js';
import { Results } from '../components/Results.js';
import { Turnstile } from '../components/Turnstile.js';
import { SITE_NAME } from '../components/Layout.js';
import { api, ApiError } from '../lib/api.js';
import { useSeo } from '../lib/seo.js';
import { useSettings } from '../lib/state.js';
import { readClientSession, saveClientSession } from '../lib/clientSession.js';
import { useAdoptEarlyInput } from '../lib/earlyInput.js';

// Ported from prototype/src/pages/Home.jsx (design C approved). The mock agent is replaced by
// POST /api/v1/chat/search; the conversation itself lives only in this component's memory —
// it is never stored, logged or put in browser storage (CLAUDE.md rule 8).

const EXAMPLES = [
  'My marriage keeps getting delayed and my family is worried',
  'I lost my job and need career guidance, I live in Bengaluru',
  'Having relationship problems with my partner, online is fine',
  'Business losses for the last year, looking for vastu advice in Mumbai',
];

const THINKING = [
  'Listening to your concern…',
  'Matching astrologers registered with us…',
  'Checking their websites, specialities and client reviews…',
  'Preparing your shortlist…',
];

export const FAQ: [string, string][] = [
  [
    'How does the AI find the right astrologer?',
    'You describe your problem to the Rishi, our AI guide. It works out what kind of help you need and where you are, then matches you with verified astrologers who specialise in exactly that, ranked by real client reviews.',
  ],
  [
    'Is it free?',
    `Yes. Searching and contacting astrologers through ${SITE_NAME} is free for you. You pay the astrologer directly for your consultation.`,
  ],
  [
    'Are the astrologers verified?',
    'Every astrologer has a verified business email, a website that passed our SEO audit, and reviews collected from their own clients through one-time links.',
  ],
  [
    'Can I consult online?',
    'Many astrologers offer phone and video consultations, so you can consult someone anywhere in the world. Just tell the Rishi "online is fine".',
  ],
];

type NewMessage =
  | { role: 'user' | 'agent'; type: 'text'; text: string; chips?: string[] }
  | { role: 'agent'; type: 'results'; data: ChatSearchResponse };
type Message = NewMessage & { id: number };

let nextMessageId = 1;

function asLayout(v: string | null, fallback: HomeLayout): HomeLayout {
  return v === 'a' || v === 'b' || v === 'c' ? v : fallback;
}

// Free text typed at the location step: a quick reply, a place we recognise, or taken as a city.
function locationFromText(t: string): ClientLocation {
  const parsed = parseLocationChoice(t) ?? detectLocation(t);
  if (parsed) return parsed;
  return { online: false, city: displayCity(t) };
}

// The conversation, kept in this tab's memory only — never in browser storage or on the server
// (CLAUDE.md rule 8) — so browser Back from a profile returns to the same chat and results
// instead of an empty page (LANE-D-4). A reload or a new tab starts afresh. Written only in the
// browser (effects never run during SSR), so no conversation is shared between server requests.
interface ChatMemory {
  messages: Message[];
  stage: 'problem' | 'location';
  pendingProblem: string;
  location: ClientLocation | null;
  lastProblem: string;
}
let chatMemory: ChatMemory | null = null;

export function Home() {
  const settings = useSettings();
  const { search: qs } = useLocation();
  const [messages, setMessages] = useState<Message[]>(() => chatMemory?.messages ?? []);
  const [input, setInput] = useState('');
  const [stage, setStage] = useState<'problem' | 'location'>(() => chatMemory?.stage ?? 'problem');
  const [pendingProblem, setPendingProblem] = useState(() => chatMemory?.pendingProblem ?? '');
  const [location, setLocation] = useState<ClientLocation | null>(
    () => chatMemory?.location ?? null,
  );
  const [thinking, setThinking] = useState(-1);
  const [emptyHint, setEmptyHint] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useAdoptEarlyInput(inputRef, setInput);
  const lastProblem = useRef(chatMemory?.lastProblem ?? '');

  useEffect(() => {
    chatMemory = { messages, stage, pendingProblem, location, lastProblem: lastProblem.current };
  }, [messages, stage, pendingProblem, location]);
  // Turnstile (when configured) marks this tab's client session as human; only then are Ad clicks
  // charged. Search works either way, so the widget never blocks the chat.
  const [humanCheckNeeded, setHumanCheckNeeded] = useState(Boolean(settings.turnstileSiteKey));

  function onTurnstileToken(token: string | null) {
    if (!token) return;
    api<ClientSessionResponse>('POST', '/api/v1/client-session/verify', {
      turnstileToken: token,
      clientSession: readClientSession(),
    })
      .then((r) => {
        saveClientSession(r);
        setHumanCheckNeeded(r.humanCheckNeeded);
      })
      .catch(() => undefined);
  }

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // When results arrive, bring the top of the results into view; otherwise follow the latest message.
    const res = el.querySelectorAll<HTMLElement>('.results');
    const lastIsResults = messages.length >= 2 && messages[messages.length - 2]?.type === 'results';
    const last = res[res.length - 1];
    if (lastIsResults && last) el.scrollTo({ top: last.offsetTop - 90, behavior: 'smooth' });
    else el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, thinking]);

  const push = (...ms: NewMessage[]) => {
    setMessages((prev) => {
      if (prev.length === 0) window.scrollTo({ top: 0 });
      return [...prev, ...ms.map((m): Message => ({ ...m, id: nextMessageId++ }))];
    });
  };
  const say = (text: string, chips?: string[]) => {
    push({ role: 'agent', type: 'text', text, ...(chips ? { chips } : {}) });
  };

  // `retry` is what to put back if the search fails, so the person can simply send again
  // (LANE-D-21): the text they typed, and the concern that was pending before it.
  async function search(
    problem: string,
    loc: ClientLocation | null,
    retry: { text: string; pending: string } = { text: '', pending: '' },
  ) {
    setThinking(0);
    let step = 0;
    const timer = setInterval(() => {
      step = Math.min(step + 1, THINKING.length - 1);
      setThinking(step);
    }, 650);
    try {
      const data = await api<ChatSearchResponse>('POST', '/api/v1/chat/search', {
        message: problem,
        ...(loc ? { location: loc } : {}),
        clientSession: readClientSession(),
      });
      saveClientSession(data);
      setHumanCheckNeeded(data.humanCheckNeeded);
      if (data.needsTopic) {
        setPendingProblem(problem);
        say(
          'Thanks for sharing. Could you tell me a little more — is this about marriage, relationships, career, business, money, health, family or something else?',
          TOPIC_QUICK_REPLIES,
        );
        return;
      }
      if (data.needsLocation) {
        setPendingProblem(problem);
        setStage('location');
        say(
          'Understood. Where are you based? Many astrologers consult online, so "online is fine" works too.',
          LOCATION_QUICK_REPLIES,
        );
        return;
      }
      lastProblem.current = problem;
      setPendingProblem('');
      setStage('problem');
      const n = data.topMatches.length + data.highlyRated.length;
      push(
        {
          role: 'agent',
          type: 'text',
          text: n
            ? `I found ${String(n)} verified astrologer${n === 1 ? '' : 's'} whose specialities, website and client ratings fit your problem.`
            : "I couldn't find a verified astrologer for this problem and location yet.",
        },
        { role: 'agent', type: 'results', data },
        {
          role: 'agent',
          type: 'text',
          text: 'Want to describe another concern, or search in a different place?',
          chips: ['Change location', 'New search'],
        },
      );
    } catch (err) {
      setPendingProblem(retry.pending);
      if (retry.text) setInput(retry.text);
      say(
        err instanceof ApiError && err.status === 429
          ? 'You are searching very quickly — please wait a minute and try again.'
          : "Sorry — I couldn't search just now. Please try again in a moment.",
      );
    } finally {
      clearInterval(timer);
      setThinking(-1);
    }
  }

  function handleUser(text: string) {
    const t = text.trim();
    if (!t || thinking >= 0) return;
    push({ role: 'user', type: 'text', text: t });
    setInput('');

    if (t === 'New search') {
      setPendingProblem('');
      setStage('problem');
      say("Sure — tell me what's on your mind.");
      return;
    }
    if (t === 'Change location') {
      setPendingProblem(lastProblem.current);
      setStage('location');
      say('Where should I look?', CHANGE_LOCATION_QUICK_REPLIES);
      return;
    }

    if (stage === 'location') {
      const place = parseLocationChoice(t) ?? detectLocation(t);
      // "Actually no, it's about my marriage" at the location question is a new concern, not a
      // city (LANE-D-1): search that, and the Rishi asks for the place again if it needs one.
      if (!place && detectIntents(t).length > 0) {
        setPendingProblem('');
        setStage('problem');
        void search(t, location, { text: t, pending: pendingProblem });
        return;
      }
      const loc = place ?? locationFromText(t);
      setLocation(loc);
      if (pendingProblem) {
        void search(pendingProblem, loc, { text: t, pending: pendingProblem });
      } else {
        setStage('problem');
        say(`Got it — ${locationLabel(loc)}. Now tell me what's troubling you.`);
      }
      return;
    }

    const place = detectLocation(t);
    // "What about Pune?" / "aur Pune me koi hai?" after results: the same concern in the new
    // place, instead of asking for the topic again (LANE-D-2).
    if (place && !pendingProblem && lastProblem.current && detectIntents(t).length === 0) {
      setLocation(place);
      void search(lastProblem.current, place, { text: t, pending: '' });
      return;
    }
    const problem = pendingProblem ? `${pendingProblem} ${t}` : t;
    const loc = place ?? location;
    if (loc) setLocation(loc);
    void search(problem, loc, { text: t, pending: pendingProblem });
  }

  // The button always looks active: an empty Send tells the person what to do, rather than doing
  // nothing the way a disabled button would.
  function trySend() {
    if (!input.trim()) {
      setEmptyHint(true);
      inputRef.current?.focus();
      return;
    }
    handleUser(input);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    trySend();
  }

  const started = messages.length > 0;
  const design = asLayout(new URLSearchParams(qs).get('design'), settings.homeLayout);
  const base = settings.siteUrl;

  useSeo({
    title: `AI Astrologer Search – Find Top-Rated, Verified Astrologers | ${SITE_NAME}`,
    description:
      'Tell our AI Rishi your problem — marriage, career, health, family — and get matched instantly with verified, top-rated astrologers. Free, private, online or near you.',
    canonical: `${base}/`,
    image: `${base}/brand/og-default.png`,
    preloadImage: {
      href: `${base}/brand/rishi-800.avif`,
      imagesrcset: [400, 800, 1200]
        .map((w) => `${base}/brand/rishi-${String(w)}.avif ${String(w)}w`)
        .join(', '),
      imagesizes: '(max-width: 640px) 200px, (max-width: 1000px) 220px, 300px',
      type: 'image/avif',
    },
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          '@id': `${base}/#website`,
          url: `${base}/`,
          name: SITE_NAME,
          inLanguage: 'en',
        },
        {
          '@type': 'Organization',
          '@id': `${base}/#org`,
          name: SITE_NAME,
          url: `${base}/`,
          logo: `${base}/brand/org-logo-512.png`,
          parentOrganization: {
            '@type': 'Organization',
            name: 'Cyntra360Hub (OPC) Private Limited',
          },
        },
        {
          '@type': 'FAQPage',
          mainEntity: FAQ.map(([q, a]) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a },
          })),
        },
      ],
    },
  });

  const lastMessage = messages[messages.length - 1];

  return (
    <div className="home">
      <section className={`hero hv-${design}${started ? ' hero-started' : ''}`}>
        <div className="stars-bg" aria-hidden="true" />
        <div className="wrap">
          {!started && (
            <div className="hero-copy">
              <div className="eyebrow">AI-enabled astrologer search</div>
              <h1>
                Meet the <span className="gold">Astrologer Search Agent</span> meant for you.
              </h1>
              <p className="lede">
                Our AI understands your problem and instantly searches verified, top-rated
                astrologers to connect you with the one best suited to guide you — free, private,
                and in minutes.
              </p>
              <div className="trust-row">
                <span>✓ Verified astrologers</span>
                <span>★ Real client reviews</span>
                <span>🔒 Private &amp; free for you</span>
              </div>
            </div>
          )}

          <div className="hero-stage">
            <div className="rishi-stage" aria-hidden="true">
              <Moon className="float f1" />
              <Sparkle className="float f2" />
              <Diya className="float f3" />
              <Lotus className="float f4" />
              <Kalash className="float f5" />
              <Sparkle className="float f6" />
              <picture>
                <source
                  type="image/avif"
                  srcSet="/brand/rishi-400.avif 400w, /brand/rishi-800.avif 800w, /brand/rishi-1200.avif 1200w"
                  sizes="(max-width: 640px) 200px, (max-width: 1000px) 220px, 300px"
                />
                <source
                  type="image/webp"
                  srcSet="/brand/rishi-400.webp 400w, /brand/rishi-800.webp 800w, /brand/rishi-1200.webp 1200w"
                  sizes="(max-width: 640px) 200px, (max-width: 1000px) 220px, 300px"
                />
                <img
                  className="rishi-art"
                  src="/brand/rishi-800.png"
                  srcSet="/brand/rishi-400.png 400w, /brand/rishi-800.png 800w, /brand/rishi-1200.png 1200w"
                  sizes="(max-width: 640px) 200px, (max-width: 1000px) 220px, 300px"
                  width={300}
                  height={278}
                  alt="The AstrologerMatch Rishi, your AI guide"
                  fetchPriority="high"
                />
              </picture>
              <div className="rishi-caption">“Speak freely, my child. Help is near.”</div>
            </div>

            <div className={`chat${started ? ' chat-open' : ''}`}>
              <div className="chat-head">
                <div className="chat-avatar">
                  <RishiAvatar alt="Rishi" />
                </div>
                <div>
                  <div className="chat-title">Rishi · your AI guide</div>
                  <div className="tiny muted-light">
                    <span className="online-dot" /> Listening now
                  </div>
                </div>
              </div>

              <div className="chat-body" ref={scrollRef} aria-live="polite">
                {!started && (
                  <div className="msg agent">
                    <div className="msg-row">
                      <div className="msg-avatar">
                        <RishiAvatar />
                      </div>
                      <div className="bubble">
                        Namaste 🙏 I am here to listen. Tell me what is troubling you — marriage,
                        career, health, family — and I will find the right astrologer to guide you.
                      </div>
                    </div>
                    <div className="example-list">
                      {EXAMPLES.map((e) => (
                        <button
                          key={e}
                          type="button"
                          className="example"
                          onClick={() => {
                            handleUser(e);
                          }}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {messages.map((m) =>
                  m.type === 'results' ? (
                    <Results key={m.id} data={m.data} />
                  ) : (
                    <div key={m.id} className={`msg ${m.role}`}>
                      {m.role === 'agent' ? (
                        <div className="msg-row">
                          <div className="msg-avatar">
                            <RishiAvatar />
                          </div>
                          <div className="bubble">{m.text}</div>
                        </div>
                      ) : (
                        <div className="bubble">{m.text}</div>
                      )}
                      {m.chips && m === lastMessage && thinking < 0 && (
                        <div className="quick">
                          {m.chips.map((c) => (
                            <button
                              key={c}
                              type="button"
                              className="quick-chip"
                              onClick={(e) => {
                                handleUser(c);
                                // Chosen by keyboard (detail 0): keep focus in the chat, not
                                // on the page body (LANE-D-15). A tap doesn't pop the keyboard.
                                if (e.detail === 0) inputRef.current?.focus();
                              }}
                            >
                              {c}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ),
                )}
                {thinking >= 0 && (
                  <div className="msg agent">
                    <div className="msg-row">
                      <div className="msg-avatar">
                        <RishiAvatar />
                      </div>
                      <div className="bubble thinking" role="status">
                        <span className="dots" aria-hidden="true">
                          <i />
                          <i />
                          <i />
                        </span>
                        {THINKING[thinking]}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <form className="chat-input" onSubmit={onSubmit}>
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={input}
                  aria-label="Your message to the Rishi"
                  maxLength={2000}
                  placeholder={
                    stage === 'location'
                      ? 'Type your city or country…'
                      : 'Tell the Rishi what is troubling you…'
                  }
                  onChange={(e) => {
                    setInput(e.target.value);
                    setEmptyHint(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      trySend();
                    }
                  }}
                  aria-describedby={emptyHint ? 'chat-empty-hint' : undefined}
                />
                {/* Always the brand gold — an empty message gets a hint on Send, not a greyed-out
                    button that looks broken (only "already sending" disables it). */}
                <button type="submit" className="btn btn-gold btn-send" disabled={thinking >= 0}>
                  Send
                </button>
              </form>
              {emptyHint && (
                <p id="chat-empty-hint" className="small warn" role="alert">
                  Type your message first, then press Send.
                </p>
              )}
              {humanCheckNeeded && settings.turnstileSiteKey && (
                <Turnstile siteKey={settings.turnstileSiteKey} onToken={onTurnstileToken} />
              )}
            </div>
          </div>
          <p className="tiny muted-light center mt16">
            The Rishi is an AI guide that helps you find a real astrologer. Your conversation is
            private and used only for this search.
          </p>
        </div>
      </section>

      <section className="wrap how">
        <h2>How it works</h2>
        <div className="how-grid">
          <div className="how-card">
            <span className="how-n">1</span>
            <h3>Talk to AI</h3>
            <p>
              Describe your problem in your own words — marriage, career, health, business, family.
              No forms.
            </p>
          </div>
          <div className="how-card">
            <span className="how-n">2</span>
            <h3>AI searches top-rated astrologers</h3>
            <p>
              Our AI compares verified astrologers&apos; specialities, client reviews and experience
              to find who handles exactly your kind of problem.
            </p>
          </div>
          <div className="how-card">
            <span className="how-n">3</span>
            <h3>Contact them directly</h3>
            <p>
              Call, WhatsApp or visit their website. You pay the astrologer directly — never us.
            </p>
          </div>
        </div>
      </section>

      <section className="wrap faq">
        <h2>Questions people ask</h2>
        <div className="faq-list">
          {FAQ.map(([q, a]) => (
            <details key={q}>
              <summary>{q}</summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="wrap cta-band">
        <div>
          <h2>Are you an astrologer?</h2>
          <p>
            Get recommended by our AI to clients looking for your speciality — anywhere in the
            world. Registration includes a <b>free SEO audit of your website</b>, with a clear list
            of what to fix.
          </p>
        </div>
        <Link to="/astrologer" className="btn btn-gold btn-lg">
          List your practice
        </Link>
      </section>
    </div>
  );
}
